import { useEffect, useRef } from 'react';

const enum Dimension {
    WIDTH = 'Width',
    HEIGHT = 'Height',
}

type SizeProp =
    | 'clientWidth'
    | 'clientHeight'
    | 'offsetWidth'
    | 'offsetHeight'
    | 'scrollWidth'
    | 'scrollHeight';

export function useResize(onResize: (size: { width: number; height: number }) => void) {
    const previous = useRef({ width: 0, height: 0 });

    useEffect(() => {
        const handleChange = () => {
            const width = _getMaxSizing(Dimension.WIDTH);
            const height = _getMaxSizing(Dimension.HEIGHT);

            if (previous.current.width === width && previous.current.height === height) {
                return;
            }

            previous.current = { width, height };

            onResize({
                width: width,
                height: height,
            });
        };

        handleChange();

        const mutationObserver = new MutationObserver(() => {
            handleChange();
        });

        mutationObserver.observe(document.body, { subtree: true, childList: true });

        return () => mutationObserver.disconnect();
    }, [onResize]);
}

function _getMaxSizing(dimension: Dimension) {
    const properties = ['scroll', 'offset', 'client'].map((prefix) => `${prefix}${dimension}`);

    const values = properties
        .map((prop) => document.body[prop as SizeProp])
        .filter((num) => !isNaN(num));

    return Math.max(...values);
}
